import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { CountdownTimer } from '@thd-olt-component-react/countdown-timer';
import { useBreakpoint, createTheme, ThemeProvider } from '@one-thd/sui-atomic-components';
import { useImpression } from '@thd-olt-component-react/impression';
import classNames from 'classnames';

const EventCountdownTimer = ({
  endDate = null,
  eventMessage = 'Event Ends In:',
  eventLink = null,
  backgroundImage = '',
  componentId,
  mobileFontSize,
  enableB2CRadius = false,
  eventIcon = null,
  eventLinkButtonText = 'Shop Now',
  isGenericCountdownTimer = false
}) => {
  useEffect(() => {
    if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.trigger) {
      window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('event-countdown-timer.ready');
    }
  }, []);
  const { lesser: isMobile } = useBreakpoint('md');

  // Extract page ID from eventLink (e.g., "n-5yc1v" or "shopall")
  const getPageId = (link) => {
    if (!link) return 'shopnow';
    const match = link.match(/\/([nN]-[\w]+)|\/(\w+)\/?$/);
    return match ? (match[1] || match[2]).toLowerCase() : 'shopnow';
  };

  // eslint-disable-next-line max-len

  const { ref, clickID } = useImpression({
    data: {
      id: componentId,
      name: 'EventCountdownTimer',
      component: 'EventCountdownTimer',
      type: 'content',
      position: 1,
      category: getPageId(eventLink)
    }
  });

  if (!endDate) {
    return null;
  }

  const style = {
    wrapper: classNames(
      'sui-block',
      'sui-p-3',
      'sui-py-8',
      'sui-bg-[#64994B]',
      'sui-h-full',
      'sui-bg-cover',
      'sui-bg-left',
      'hover:sui-no-underline',
      { '!sui-rounded-[12px]': enableB2CRadius }
    )
  };

  const EventCountdownTimerThemed = createTheme({
    palette: {
      mode: 'light',
      text: {
        primary: 'inverse'
      }
    }
  });

  const fontSize = isMobile ? mobileFontSize : 'base';

  return (
    <div
      data-component="EventCountdownTimer"
      ref={ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={clickID}
    >
      <ThemeProvider theme={EventCountdownTimerThemed}>
        {isGenericCountdownTimer ? (
          <div
            className={classNames(
              'sui-bg-cover',
              'sui-bg-left',
              'sui-p-6',
              'lg:sui-px-8',
              'sui-bg-[#64994B]',
              'sui-py-8',
              { '!sui-rounded-[12px]': enableB2CRadius }
            )}
            // eslint-disable-next-line react/style-prop-object
            style={{ backgroundImage: `url(${backgroundImage})` }}
          >
            <CountdownTimer
              endDate={endDate}
              customMessagingString={eventMessage}
              fontSize={fontSize}
              invertText
              shouldUseMessagingString
              shouldIncludeSvg
              shouldUseStackedTimeUnits
              eventIcon={eventIcon}
              eventLinkButton={eventLink ? { label: eventLinkButtonText || 'Shop Now', link: eventLink } : null}
              hideIconBelowBreakpoint="lg"
            />
          </div>
        ) : (
        // Legacy clickable timer (no icon)
          <a
            aria-label="Event Countdown Timer"
            href={eventLink}
            className={style.wrapper}
            // eslint-disable-next-line react/style-prop-object
            style={{ backgroundImage: `url(${backgroundImage})` }}
            alt="Event Countdown Timer"
          >
            <CountdownTimer
              endDate={endDate}
              customMessagingString={eventMessage}
              fontSize={fontSize}
              invertText
              shouldUseMessagingString
              shouldIncludeSvg
              shouldEventTimer
            />
          </a>
        )}
      </ThemeProvider>
    </div>
  );
};

EventCountdownTimer.propTypes = {
  componentId: PropTypes.string.isRequired,
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  eventMessage: PropTypes.string,
  eventLink: PropTypes.string,
  backgroundImage: PropTypes.string,
  mobileFontSize: PropTypes.string,
  enableB2CRadius: PropTypes.bool,
  eventIcon: PropTypes.string,
  eventLinkButtonText: PropTypes.string,
  isGenericCountdownTimer: PropTypes.bool
};

EventCountdownTimer.displayName = 'EventCountdownTimer';

export { EventCountdownTimer };
